import React, { useCallback, useEffect, useState } from "react";
import { equals, mergeRight } from "ramda";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContentText from "@material-ui/core/DialogContentText";
import TextField from "@material-ui/core/TextField";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";

import DialogContent from "@material-ui/core/DialogContent";
import { lensProp, pick, set } from "ramda";
import Grid from "@material-ui/core/Grid";
import { FetchError } from "../../util/fetchUtils";
import { useDispatch } from "react-redux";
import { logOut } from "../../auth/authActions";
import MuiAlert from "@material-ui/lab/Alert";
import Snackbar from "@material-ui/core/Snackbar";
import { isBlank, notBlank } from "../../util/ext";
import { useIpOverrideCreate } from "./ipOverrideServices";
import { Skeleton } from "@material-ui/lab";
import Autocomplete from "@material-ui/lab/Autocomplete";

const Alert = React.forwardRef((props, ref) => {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const initialState = {
  fields: {
    ipAddress: "",
    region: {},
  },
};

const EditIpOverride = ({ override, regions, open, onClose, onSave }) => {
  const dispatch = useDispatch();
  const [fields, setFields] = useState(initialState.fields);
  const [errorAlert, setErrorAlert] = useState({ open: false });

  useEffect(() => {
    if (override && override.ipAddress) {
      setFields(pick(["ipAddress", "region"], override));
    } else {
      setFields(initialState.fields);
    }
  }, [regions, override]);

  const onFieldChanged = field => (event, optVal) => {
    const value = event.target.value || optVal;
    console.log(value);
    const update = set(lensProp(field), value, fields);
    setFields(update);
  };

  const showAlert = message =>
    setErrorAlert({
      open: true,
      message,
    });

  const ipOverrideCreate = useIpOverrideCreate();
  const createOverride = async override => {
    if (notBlank(override.ipAddress) && notBlank(override.regionId)) {
      await ipOverrideCreate.mutateAsync({ data: override });
    } else {
      throw new Error("Both IP Address and Region are required.");
    }
  };

  const handleError = (message, err) => {
    if (err instanceof FetchError) {
      if (err.status === 401) {
        dispatch(logOut());
      } else {
        showAlert(message);
      }
    } else {
      showAlert(err.message);
    }
  };

  const handleOnSave = async () => {
    const mergedOverride = {
      ipAddress: fields.ipAddress,
      regionId: fields.region.regionId,
    };

    try {
      await createOverride(mergedOverride);
      onSave(mergedOverride);
    } catch (err) {
      handleError("Unable to save Application Account.", err);
    }
  };

  const handleOnClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleOnClose}
      aria-labelledby="form-dialog-title"
    >
      {notBlank(override) && notBlank(override.ipAddress) ? (
        <DialogTitle id="form-dialog-title">
          IP Override ({override.ipAddress})
        </DialogTitle>
      ) : (
        <DialogTitle id="form-dialog-title">New IP Override</DialogTitle>
      )}
      <DialogContent>
        <DialogContentText>Enter IP Override Information.</DialogContentText>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              autoFocus
              id="ipAddress"
              label="IP Address"
              fullWidth
              value={fields.ipAddress || ""}
              onChange={onFieldChanged("ipAddress")}
            />
          </Grid>
          <Grid item xs={12}>
            {isBlank(regions) ? (
              <Skeleton variant="text" />
            ) : (
              <Autocomplete
                disableClearable
                options={regions}
                value={fields.region || ""}
                getOptionLabel={r => (notBlank(r) ? r.label : "")}
                getOptionSelected={(opt, val) =>
                  isBlank(val) || equals(opt.regionId, val.regionId)
                }
                onChange={onFieldChanged("region")}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = null;
                  return (
                    <TextField
                      {...params}
                      type="text"
                      name={"ip-override-region-" + Math.random()}
                      variant="standard"
                      label="Region"
                      fullWidth
                    />
                  );
                }}
              />
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleOnClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleOnSave} color="primary">
          Save
        </Button>
      </DialogActions>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={errorAlert.open}
        autoHideDuration={6000}
        onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
      >
        <Alert
          onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
          severity="error"
        >
          {errorAlert.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default EditIpOverride;
